<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { EyeIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import useImpersonation from '@/composables/useImpersonation'
import { formatGrantUntil } from '@/native/supportAccess'

const { impersonation, exitImpersonating, supportViewMode, supportViewUntil } = useImpersonation()

/** Support view (email hidden) or full access the customer approved; nothing extra for an unrestricted session. */
const modeLabel = computed(() => {
    if (supportViewMode.value === 'limited') return 'Support view · email hidden'
    if (supportViewMode.value !== 'full') return ''
    const until = formatGrantUntil(supportViewUntil.value, Date.now())
    return until ? `Full access approved by the customer until ${until}` : 'Full access approved by the customer'
})

const exiting = ref(false)
const sandboxMenuOpen = ref(false)

const isSandbox = computed(() => Boolean(impersonation.value?.sandbox))

const sandboxTitle = computed(() =>
    impersonation.value?.forceOnboarded ? 'Sandbox workspace' : 'Onboarding sandbox',
)

async function onExit() {
    if (exiting.value) return
    exiting.value = true
    await exitImpersonating()
}

function closeSandboxMenu() {
    if (exiting.value) return
    sandboxMenuOpen.value = false
}

function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && sandboxMenuOpen.value) {
        e.stopPropagation()
        closeSandboxMenu()
    }
}

watch(sandboxMenuOpen, (open) => {
    if (open) window.addEventListener('keydown', onKeydown)
    else window.removeEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <div
    v-if="impersonation && !isSandbox"
    class="impersonation-banner shrink-0 z-[10000] bg-orange-dark text-white shadow-md"
  >
    <div class="flex items-center justify-center gap-3 px-4 py-2 text-sm">
      <EyeIcon class="w-4 h-4 flex-shrink-0" />
      <span class="truncate">
        Viewing as <span class="font-semibold">{{ impersonation.targetName }}</span>
        ({{ impersonation.targetEmail }})
      </span>
      <span
        v-if="modeLabel"
        class="flex-shrink-0 rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium"
        :data-support-view-mode="supportViewMode"
        data-testid="impersonation-access-mode"
      >{{ modeLabel }}</span>
      <button
        class="flex-shrink-0 rounded-md bg-white/20 hover:bg-white/30 transition-colors px-3 py-1 text-xs font-medium disabled:opacity-60"
        :disabled="exiting"
        @click="onExit"
      >
        {{ exiting ? 'Exiting…' : 'Exit impersonation' }}
      </button>
    </div>
  </div>

  <Teleport to="body">
    <template v-if="impersonation && isSandbox">
      <Transition name="sandbox-overlay">
        <div
          v-if="sandboxMenuOpen"
          class="fixed inset-0 z-[10000] bg-neutral-950/20 backdrop-blur-[1px]"
          @click="closeSandboxMenu"
        />
      </Transition>

      <div
        class="fixed z-[10001] flex flex-col items-end gap-2"
        :style="{
          right: 'max(0.75rem, env(safe-area-inset-right, 0px))',
          bottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
        }"
      >
        <Transition name="sandbox-panel">
          <div
            v-if="sandboxMenuOpen"
            class="w-[260px] rounded-xl bg-white shadow-[0_10px_40px_-10px_rgba(0,0,0,0.18),0_2px_8px_-2px_rgba(0,0,0,0.06)] ring-1 ring-neutral-200/70 p-4"
            role="dialog"
            aria-modal="true"
            :aria-label="sandboxTitle"
            @click.stop
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="text-sm font-semibold text-primary">{{ sandboxTitle }}</p>
                <p class="mt-1 text-xs text-tertiary leading-relaxed">
                  Viewing as
                  <span class="font-semibold text-secondary break-all">{{ impersonation.targetEmail }}</span>
                </p>
              </div>
              <button
                type="button"
                class="shrink-0 -mt-1 -mr-1 p-1.5 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition"
                aria-label="Close"
                :disabled="exiting"
                @click="closeSandboxMenu"
              >
                <XMarkIcon class="w-4 h-4" />
              </button>
            </div>
            <button
              type="button"
              class="mt-4 w-full text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-md px-3 py-1.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="exiting"
              @click="onExit"
            >
              {{ exiting ? 'Exiting…' : 'Back to harness' }}
            </button>
          </div>
        </Transition>

        <button
          type="button"
          class="p-2 rounded-full"
          :aria-expanded="sandboxMenuOpen"
          :aria-label="sandboxMenuOpen ? `Close ${sandboxTitle} controls` : `Open ${sandboxTitle} controls`"
          title="Sandbox"
          @click="sandboxMenuOpen = !sandboxMenuOpen"
        >
          <span
            class="block w-2.5 h-2.5 rounded-full bg-red-500 shadow-[0_0_0_2px_rgba(255,255,255,0.95),0_0_0_3px_rgba(239,68,68,0.35)]"
          />
        </button>
      </div>
    </template>
  </Teleport>
</template>

<style scoped>
.impersonation-banner {
  padding-top: env(safe-area-inset-top, 0px);
}

.sandbox-overlay-enter-active,
.sandbox-overlay-leave-active {
  transition: opacity 0.15s ease;
}
.sandbox-overlay-enter-from,
.sandbox-overlay-leave-to {
  opacity: 0;
}

.sandbox-panel-enter-active,
.sandbox-panel-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.sandbox-panel-enter-from,
.sandbox-panel-leave-to {
  opacity: 0;
  transform: translateY(6px) scale(0.96);
}
</style>
